import { 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  User
} from 'firebase/auth';
import { auth } from '../firebase';

// Admin kullanıcı email'i - production'da env'den alınmalı

const ADMIN_EMAIL = process.env.REACT_APP_ADMIN_EMAIL;

// Maksimum giriş denemesi
const MAX_LOGIN_ATTEMPTS = 5;
const LOCKOUT_TIME = 15 * 60 * 1000; // 15 dakika

// Login denemeleri localStorage'da saklanır
const getLoginAttempts = (): { count: number; lastAttempt: number } => {
  const stored = localStorage.getItem('loginAttempts');
  return stored ? JSON.parse(stored) : { count: 0, lastAttempt: 0 };
};

const setLoginAttempts = (count: number) => {
  localStorage.setItem('loginAttempts', JSON.stringify({
    count,
    lastAttempt: Date.now()
  }));
};

const clearLoginAttempts = () => {
  localStorage.removeItem('loginAttempts');
};

export interface AuthUser {
  uid: string;
  email: string | null;
  isAdmin: boolean;
}

// Admin giriş fonksiyonu
export const signInAdmin = async (email: string, password: string): Promise<AuthUser> => {
  try {
    // Brute force kontrolü
    const attempts = getLoginAttempts();
    const now = Date.now();
    
    // Eğer son denemeden sonra çok fazla time geçmişse sayacı sıfırla
    if (now - attempts.lastAttempt > LOCKOUT_TIME) {
      clearLoginAttempts();
    } else if (attempts.count >= MAX_LOGIN_ATTEMPTS) {
      const remainingTime = Math.ceil((LOCKOUT_TIME - (now - attempts.lastAttempt)) / 60000);
      throw new Error(`Çok fazla başarısız giriş denemesi. ${remainingTime} dakika sonra tekrar deneyin.`);
    }

    // Email format kontrolü
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setLoginAttempts(attempts.count + 1);
      throw new Error('Geçersiz email format');
    }

    // Şifre uzunluk kontrolü
    if (password.length < 6) {
      setLoginAttempts(attempts.count + 1);
      throw new Error('Şifre en az 6 karakter olmalıdır');
    }
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;
    
    // Admin kontrolü
    if (user.email !== ADMIN_EMAIL) {
      await signOut(auth);
      setLoginAttempts(attempts.count + 1);
      throw new Error('Bu hesap admin yetkisine sahip değil');
    }

    clearLoginAttempts();
    
    return {
      uid: user.uid,
      email: user.email,
      isAdmin: true
    };
  } catch (error: any) {
     // Firebase hata kodları
    if (error.code === 'auth/user-not-found') {
      setLoginAttempts(getLoginAttempts().count + 1);
      throw new Error('Kullanıcı bulunamadı');
    } else if (error.code === 'auth/wrong-password') {
      setLoginAttempts(getLoginAttempts().count + 1);
      throw new Error('Yanlış şifre');
    } else if (error.code === 'auth/invalid-email') {
      setLoginAttempts(getLoginAttempts().count + 1);
      throw new Error('Geçersiz email adresi');
    } else if (error.code === 'auth/too-many-requests') {
      throw new Error('Çok fazla başarısız deneme. Lütfen daha sonra tekrar deneyin.');
    }
    
    // Diğer hatalar
    if (!error.message.includes('dakika sonra')) {
      setLoginAttempts(getLoginAttempts().count + 1);
    }
    throw new Error(error.message || 'Giriş yapılırken bir hata oluştu');
  }
};

// Çıkış fonksiyonu
export const signOutAdmin = async (): Promise<void> => {
  try {
    await signOut(auth);
  } catch (error: any) {
    throw new Error('Çıkış yapılırken bir hata oluştu');
  }
};

// Auth durumunu dinle
export const onAuthStateChange = (callback: (user: AuthUser | null) => void) => {
  return onAuthStateChanged(auth, (user: User | null) => {
    if (user && user.email === ADMIN_EMAIL) {
      callback({
        uid: user.uid,
        email: user.email,
        isAdmin: true
      });
    } else {
      callback(null);
    }
  });
};

// Admin kontrolü
export const isAdmin = (user: User | null): boolean => {
  return user?.email === ADMIN_EMAIL;
};